import { c as _c } from "react/compiler-runtime";
import { useMemo, useRef, useState } from "react";
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { escapeRegexCharacters, getStoredStopId, hasStoredStopId, normalizeStopDescription, withStoredStopId } from "@catchy/app-core";
import CloseCross from "@catchy/assets/svgs/close-cross.svg";
import Spinner from "@catchy/assets/svgs/spinner.svg";
import StopMarker from "@catchy/assets/svgs/stop-mono-lg.svg";
import DestinationFlag from "@catchy/assets/svgs/destination-flag.svg";
import useSWR from "swr";
import Modal from "./Modal";
import DialogAction from "./DialogAction";
import IconButton from "./IconButton";
const fetcher = url => fetch(url).then(res => res.json());
function extractFavouriteStopIds(favourites) {
  if (!Array.isArray(favourites)) return [];
  return Array.from(new Set(favourites.map(favourite => {
    if (typeof favourite === "string") return favourite.trim();
    if (favourite && typeof favourite === "object") {
      const stopId = getStoredStopId(favourite);
      if (typeof stopId === "string") {
        return stopId.trim();
      }
    }
    return "";
  }).filter(Boolean)));
}
function StopOption(t0) {
  const $ = _c(19);
  const {
    stop,
    query,
    favourite,
    stopType
  } = t0;
  let t1;
  if ($[0] !== stop.stop_description) {
    t1 = normalizeStopDescription(stop.stop_description);
    $[0] = stop.stop_description;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const stopDescription = t1;
  const t2 = `mr-1 h-[1.125rem] w-3 flex-shrink-0 ${favourite ? stopType === "bus" ? "text-orange-400" : "text-green-500" : "text-zinc-300 dark:text-zinc-500"}`;
  let t3;
  if ($[2] !== t2) {
    t3 = <div className="grid h-5 w-11 shrink-0 place-items-center"><StopMarker className={t2} /></div>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== stop.preferred_name) {
    t4 = <span className="text-lg leading-tight">{stop.preferred_name}</span>;
    $[4] = stop.preferred_name;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== query || $[7] !== stop.stop_id || $[8] !== stopDescription) {
    t5 = (query && stop.stop_id.includes(query) || stopDescription) && <span className="text-sm opacity-60">{`Stop ${stop.stop_id}`}{stopDescription && ` - ${stopDescription}`}</span>;
    $[6] = query;
    $[7] = stop.stop_id;
    $[8] = stopDescription;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <div className="flex flex-col gap-y-1">{t4}{t5}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t6) {
    t7 = <div className="flex items-start px-[20px]">{t3}{t6}</div>;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== stop || $[17] !== t7) {
    t8 = <ComboboxOption key={stop.stop_id} value={stop} className="items-start data-[focus]:bg-zinc-400/10 flex cursor-pointer flex-nowrap py-3 leading-tight transition-colors duration-200 motion-reduce:transition-none hover:bg-zinc-400/10 active:bg-zinc-400/10">{t7}</ComboboxOption>;
    $[16] = stop;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
function CurrentStop(t0) {
  const $ = _c(9);
  const {
    stop,
    onClear
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mr-2 grid size-9 shrink-0 place-items-center pl-2"><DestinationFlag className="size-5" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== stop.stop_name) {
    t2 = <span className="flex-1 pt-1.5 text-lg font-semibold leading-tight">{stop.stop_name}</span>;
    $[1] = stop.stop_name;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CloseCross aria-hidden="true" className="h-[1.125rem] w-[1.125rem] text-zinc-500 dark:text-zinc-300" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onClear) {
    t4 = <IconButton label="Clear destination" variant="subtle" onClick={onClear} className="z-10">{t3}</IconButton>;
    $[4] = onClear;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <div className="items-start flex flex-nowrap justify-between px-[20px] pb-4 leading-tight">{t1}{t2}{t4}</div>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
export default function DestinationStopSelector(t0) {
  const $ = _c(81);
  const {
    selectedDestination,
    destinationModalIsOpen,
    setDestinationModalIsOpen,
    destinations,
    setDestinations,
    clearDestination,
    stopId,
    preferredStopNames,
    favourites,
    stopType,
    setPeek
  } = t0;
  const [query, setQuery] = useState("");
  const inputRef = useRef();
  let t1;
  if ($[0] !== destinations || $[1] !== stopId) {
    let t2;
    if ($[3] !== stopId) {
      t2 = destination => hasStoredStopId(destination, stopId) && destination.destination;
      $[3] = stopId;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = destinations.some(t2);
    $[0] = destinations;
    $[1] = stopId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const initialHadDestination = t1;
  let t2;
  if ($[5] !== favourites) {
    t2 = extractFavouriteStopIds(favourites);
    $[5] = favourites;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const favouriteStopIds = t2;
  const selectedDestinationStopId = selectedDestination ? getStoredStopId(selectedDestination) : null;
  const visibleSelectedDestination = selectedDestinationStopId && selectedDestinationStopId !== stopId ? selectedDestination : null;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      revalidateOnFocus: false
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    data: destinationStops,
    isLoading: isLoadingDestinationStops
  } = useSWR(destinationModalIsOpen && stopId ? `/api/stops/after/${stopId}` : null, fetcher, t3);
  let t4;
  if ($[8] !== destinationModalIsOpen || $[9] !== favouriteStopIds) {
    t4 = destinationModalIsOpen && favouriteStopIds.length ? `/api/stops/near/favourites?favourites=${encodeURIComponent(favouriteStopIds.join(","))}` : null;
    $[8] = destinationModalIsOpen;
    $[9] = favouriteStopIds;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      revalidateOnFocus: false
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    data: nearFavouriteStops,
    isLoading: isLoadingNearFavouriteStops
  } = useSWR(t4, fetcher, t5);
  let t6;
  bb0: {
    if (!destinationStops) {
      let t7;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = [];
        $[12] = t7;
      } else {
        t7 = $[12];
      }
      t6 = t7;
      break bb0;
    }
    let t7;
    if ($[13] !== destinationStops || $[14] !== preferredStopNames || $[15] !== stopId) {
      let t8;
      if ($[17] !== stopId) {
        t8 = stop => stop?.stop_id && stop.stop_id !== stopId;
        $[17] = stopId;
        $[18] = t8;
      } else {
        t8 = $[18];
      }
      let t9;
      if ($[19] !== preferredStopNames) {
        t9 = stop_0 => {
          const preferredName = preferredStopNames.find(pref => hasStoredStopId(pref, stop_0.stop_id))?.name;
          return {
            ...stop_0,
            preferred_name: preferredName || stop_0.stop_name
          };
        };
        $[19] = preferredStopNames;
        $[20] = t9;
      } else {
        t9 = $[20];
      }
      t7 = destinationStops.filter(t8).map(t9);
      $[13] = destinationStops;
      $[14] = preferredStopNames;
      $[15] = stopId;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    t6 = t7;
  }
  const destinationStopsWithPreferredNames = t6;
  let t7;
  bb1: {
    if (!destinationStopsWithPreferredNames || !destinations || !nearFavouriteStops) {
      let t8;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = [];
        $[21] = t8;
      } else {
        t8 = $[21];
      }
      t7 = t8;
      break bb1;
    }
    let t8;
    if ($[22] !== destinationStopsWithPreferredNames || $[23] !== destinations || $[24] !== nearFavouriteStops || $[25] !== stopId) {
      const destinationStopsMap = new Map(destinationStopsWithPreferredNames.map(_temp));
      const uniqueStopIds = new Set();
      destinations.forEach(dest => {
        const destinationStopId = getStoredStopId(dest.destination);
        if (destinationStopId && destinationStopId !== stopId) {
          uniqueStopIds.add(destinationStopId);
        }
      });
      nearFavouriteStops.forEach(stop_2 => {
        if (stop_2.stop_id && stop_2.stop_id !== stopId) {
          uniqueStopIds.add(stop_2.stop_id);
        }
      });
      t8 = Array.from(uniqueStopIds).map(stop_id => destinationStopsMap.get(stop_id)).filter(Boolean);
      $[22] = destinationStopsWithPreferredNames;
      $[23] = destinations;
      $[24] = nearFavouriteStops;
      $[25] = stopId;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    t7 = t8;
  }
  const suggestedStops = t7;
  let t8;
  if ($[27] !== destinationStopsWithPreferredNames || $[28] !== query) {
    bb2: {
      const escapedQuery = escapeRegexCharacters(query.trim());
      if (!escapedQuery) {
        t8 = destinationStopsWithPreferredNames;
        break bb2;
      }
      ;
      try {
        const regexes = escapedQuery.split(/\W+/).map(_temp2);
        t8 = destinationStopsWithPreferredNames.filter(match => regexes.every(regex_0 => regex_0.test(match.preferred_name)) || regexes.every(regex => regex.test(match.stop_name)) || new RegExp("^" + escapedQuery, "i").test(match.stop_id)).slice(0, 20);
      } catch (t9) {
        const error = t9;
        console.error("Invalid search query:", error);
        let t10;
        if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = [];
          $[30] = t10;
        } else {
          t10 = $[30];
        }
        t8 = t10;
      }
    }
    $[27] = destinationStopsWithPreferredNames;
    $[28] = query;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const searchResults = t8;
  let t9;
  if ($[31] !== destinations || $[32] !== setDestinationModalIsOpen || $[33] !== setDestinations || $[34] !== setPeek || $[35] !== stopId) {
    t9 = destination_0 => {
      if (!destination_0?.stop_id || destination_0.stop_id === stopId) {
        setQuery("");
        setDestinationModalIsOpen(false);
        return;
      }
      const destinationToSave = withStoredStopId({
        stop_name: destination_0.stop_name
      }, destination_0.stop_id);
      setQuery(destination_0.stop_name);
      const currentDestinations = Array.isArray(destinations) ? destinations : [];
      const updatedDestinations = currentDestinations.some(el_0 => hasStoredStopId(el_0, stopId)) ? currentDestinations.map(el => hasStoredStopId(el, stopId) ? withStoredStopId({
        ...el,
        destination: destinationToSave
      }, stopId) : el) : [...currentDestinations, withStoredStopId({
        destination: destinationToSave
      }, stopId)];
      setDestinations(updatedDestinations.filter(_temp3));
      setQuery("");
      setDestinationModalIsOpen(false);
      setPeek(false);
    };
    $[31] = destinations;
    $[32] = setDestinationModalIsOpen;
    $[33] = setDestinations;
    $[34] = setPeek;
    $[35] = stopId;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  const saveDestination = t9;
  let t10;
  if ($[37] !== setDestinationModalIsOpen) {
    t10 = () => {
      setQuery("");
      setDestinationModalIsOpen(false);
    };
    $[37] = setDestinationModalIsOpen;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== saveDestination) {
    t11 = destination_1 => {
      if (destination_1) {
        saveDestination(destination_1);
      }
    };
    $[39] = saveDestination;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = event => setQuery(event.target.value);
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  let t14;
  if ($[42] !== query) {
    t13 = <ComboboxInput aria-label="Search destination stops" className="h-12 w-full bg-transparent py-2 pl-2.5 placeholder-zinc-400 focus:outline-none" displayValue={_temp4} value={query} onChange={t12} placeholder="Search a stop name or stop ID" ref={inputRef} />;
    t14 = query && <IconButton onClick={() => {
      inputRef.current.focus();
      setQuery("");
    }} label="Clear search" variant="subtle" className="top-1.5 z-10"><CloseCross aria-hidden="true" className="h-[1.125rem] w-[1.125rem] text-zinc-500 dark:text-zinc-300" /></IconButton>;
    $[42] = query;
    $[43] = t13;
    $[44] = t14;
  } else {
    t13 = $[43];
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t13 || $[46] !== t14) {
    t15 = <div className="relative px-[20px] pb-4"><div className="transition-colors group flex h-12 w-full appearance-none items-center rounded border-none bg-transparent pr-2 text-lg outline-none ring-1 ring-zinc-500/50 duration-100 focus-within:ring focus-within:ring-blue-500/50">{t13}{t14}</div></div>;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== clearDestination || $[49] !== favourites || $[50] !== isLoadingDestinationStops || $[51] !== isLoadingNearFavouriteStops || $[52] !== query || $[53] !== searchResults || $[54] !== setDestinationModalIsOpen || $[55] !== stopType || $[56] !== suggestedStops || $[57] !== visibleSelectedDestination) {
    t16 = query.length ? isLoadingDestinationStops ? <div className="px-[20px] text-lg opacity-60">Loading...</div> : searchResults.length > 0 ? searchResults.map((stop_4, key) => <StopOption key={key} stop={stop_4} query={query} favourite={favourites?.includes(stop_4.stop_id)} stopType={stopType} />) : <div className="px-[20px] text-lg opacity-60">No matching stops found.</div> : <div>{visibleSelectedDestination && <><h3 className="px-[20px] pb-1 font-semibold opacity-60">Current Destination</h3><CurrentStop stop={visibleSelectedDestination} onClear={() => {
          clearDestination();
          setQuery("");
          setDestinationModalIsOpen(false);
        }} /></>}{isLoadingNearFavouriteStops && <div className="flex items-center px-[20px] pb-2 font-semibold opacity-60"><Spinner className="mr-2 size-4 animate-spin" />Loading suggested destinations...</div>}{suggestedStops.length > 0 && <><h3 className="px-[20px] pb-2 font-semibold opacity-60">Suggested Destinations</h3>{suggestedStops.map((dest_0, key_0) => <StopOption key={key_0} stop={dest_0} query={query} favourite={favourites?.includes(dest_0.stop_id)} stopType={stopType} />)}</>}</div>;
    $[48] = clearDestination;
    $[49] = favourites;
    $[50] = isLoadingDestinationStops;
    $[51] = isLoadingNearFavouriteStops;
    $[52] = query;
    $[53] = searchResults;
    $[54] = setDestinationModalIsOpen;
    $[55] = stopType;
    $[56] = suggestedStops;
    $[57] = visibleSelectedDestination;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  let t17;
  if ($[59] !== t16) {
    t17 = <ComboboxOptions modal={false} className="flex-grow overflow-auto py-1 text-base focus:outline-none sm:text-sm" static={true}>{t16}</ComboboxOptions>;
    $[59] = t16;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  let t18;
  if ($[61] !== t11 || $[62] !== t15 || $[63] !== t17) {
    t18 = <Combobox onChange={t11}>{t15}{t17}</Combobox>;
    $[61] = t11;
    $[62] = t15;
    $[63] = t17;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== setDestinationModalIsOpen) {
    t19 = <DialogAction onClick={() => {
      setQuery("");
      setDestinationModalIsOpen(false);
    }}>Cancel</DialogAction>;
    $[65] = setDestinationModalIsOpen;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  let t20;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="flex-1" />;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  let t21;
  if ($[68] !== clearDestination || $[69] !== initialHadDestination || $[70] !== setDestinationModalIsOpen || $[71] !== visibleSelectedDestination) {
    t21 = initialHadDestination && visibleSelectedDestination && <DialogAction destructive={true} onClick={() => {
      clearDestination();
      setQuery("");
      setDestinationModalIsOpen(false);
    }}>Clear</DialogAction>;
    $[68] = clearDestination;
    $[69] = initialHadDestination;
    $[70] = setDestinationModalIsOpen;
    $[71] = visibleSelectedDestination;
    $[72] = t21;
  } else {
    t21 = $[72];
  }
  let t22;
  if ($[73] !== t19 || $[74] !== t21) {
    t22 = <div className="flex flex-row-reverse gap-2 p-3 font-semibold text-blue-500 dark:text-white">{t19}{t20}{t21}</div>;
    $[73] = t19;
    $[74] = t21;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  let t23;
  if ($[76] !== destinationModalIsOpen || $[77] !== t10 || $[78] !== t18 || $[79] !== t22) {
    t23 = <Modal isOpen={destinationModalIsOpen} onClose={t10} title="Filter by Destination Stop" fullScreen={true}>{t18}{t22}</Modal>;
    $[76] = destinationModalIsOpen;
    $[77] = t10;
    $[78] = t18;
    $[79] = t22;
    $[80] = t23;
  } else {
    t23 = $[80];
  }
  return t23;
}
function _temp4(stop_3) {
  return stop_3?.preferred_name || "";
}
function _temp3(el_1) {
  return getStoredStopId(el_1) && el_1.destination;
}
function _temp2(val) {
  return new RegExp("\\b" + val, "i");
}
function _temp(stop_1) {
  return [stop_1.stop_id, stop_1];
}